<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  mdiClose,
  mdiCheck,
  mdiAlertCircle,
  mdiFolderZipOutline,
  mdiMovieOpenOutline,
  mdiContentCut,
  mdiDownload,
  mdiRefresh,
} from '@mdi/js'
import { formatFileSize } from '@/apps/core'
import { EXPORT_JOB_DRIVERS } from '../../stores/exportJobDrivers'
import type { ExportJob, ExportJobKind, ExportJobMessage } from '../../stores/exportJobs.types'

interface Props {
  job: ExportJob
}

interface Emits {
  (e: 'remove', id: string): void
  (e: 'download', id: string): void
  (e: 'retry', id: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()

const KIND_ICONS: Record<ExportJobKind, string> = {
  zip: mdiFolderZipOutline,
  'burned-video': mdiMovieOpenOutline,
  clip: mdiContentCut,
}

const isActive = computed(
  () => props.job.status === 'pending' || props.job.status === 'running',
)

/** A message is either an i18n key to resolve now, or wording from the server. */
function resolve(message: ExportJobMessage | null): string {
  if (!message) return ''
  return 'text' in message ? message.text : t(message.key, message.params ?? {})
}

const statusText = computed(() => {
  switch (props.job.status) {
    case 'pending':
      return t('assets.transfers.exports.preparing')
    case 'running':
      return `${props.job.progress}%`
    case 'ready':
      return t('assets.transfers.exports.ready')
    case 'failed':
      return t('assets.transfers.exports.failed')
    default:
      return ''
  }
})

const detailText = computed(() => {
  if (props.job.status === 'failed') {
    return resolve(props.job.error) || t('assets.transfers.exports.errorGeneric')
  }

  // Always name what the export is, then whatever the driver has to add.
  const parts = [t(`assets.transfers.exports.kind.${props.job.kind}`)]
  if (props.job.status === 'ready' && props.job.sizeBytes) {
    parts.push(formatFileSize(props.job.sizeBytes))
  }
  const extra = resolve(props.job.phase) || resolve(props.job.detail)
  if (extra) parts.push(extra)
  return parts.join(' · ')
})

// Only offer a retry when the kind's driver knows how to relaunch itself.
const canRetry = computed(
  () => props.job.status === 'failed' && Boolean(EXPORT_JOB_DRIVERS[props.job.kind]?.restart),
)
</script>

<template>
  <div
    class="export-item"
    :class="{
      'export-item--failed': job.status === 'failed',
      'export-item--ready': job.status === 'ready',
    }"
  >
    <!-- Visual with progress -->
    <div class="export-item__visual">
      <svg v-if="isActive" class="export-item__progress-ring" viewBox="0 0 44 44">
        <circle
          class="export-item__progress-bg"
          cx="22"
          cy="22"
          r="20"
          fill="none"
          stroke-width="2"
        />
        <circle
          class="export-item__progress-bar"
          cx="22"
          cy="22"
          r="20"
          fill="none"
          stroke-width="2"
          :stroke-dasharray="125.6"
          :stroke-dashoffset="125.6 - (125.6 * job.progress) / 100"
        />
      </svg>

      <div class="export-item__thumbnail">
        <v-icon
          v-if="job.status === 'failed'"
          :icon="mdiAlertCircle"
          size="20"
          color="error"
        />
        <v-icon
          v-else-if="job.status === 'ready'"
          :icon="mdiCheck"
          size="20"
          color="success"
        />
        <v-icon v-else :icon="KIND_ICONS[job.kind]" size="20" class="export-item__icon" />
      </div>
    </div>

    <!-- Info -->
    <div class="export-item__info">
      <span class="export-item__name">{{ job.label }}</span>
      <span class="export-item__meta">
        <span class="export-item__source">
          {{ t(`assets.transfers.exports.source.${job.kind}`) }}
        </span>
        <span
          class="export-item__status"
          :class="{
            'export-item__status--error': job.status === 'failed',
            'export-item__status--success': job.status === 'ready',
          }"
        >
          {{ statusText }}
        </span>
        <template v-if="detailText">
          <span class="export-item__separator">•</span>
          <span class="export-item__detail">{{ detailText }}</span>
        </template>
      </span>
    </div>

    <!-- Actions -->
    <div class="export-item__actions">
      <v-btn
        v-if="job.status === 'ready' && job.downloadUrl"
        icon
        size="x-small"
        variant="text"
        :title="t('assets.transfers.exports.download')"
        @click="emit('download', job.id)"
      >
        <v-icon :icon="mdiDownload" size="18" />
      </v-btn>
      <v-btn
        v-if="canRetry"
        icon
        size="x-small"
        variant="text"
        :title="t('assets.transfers.exports.retry')"
        @click="emit('retry', job.id)"
      >
        <v-icon :icon="mdiRefresh" size="18" />
      </v-btn>
      <v-btn
        v-if="!isActive"
        icon
        size="x-small"
        variant="text"
        :title="t('assets.transfers.exports.remove')"
        @click="emit('remove', job.id)"
      >
        <v-icon :icon="mdiClose" size="18" />
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.export-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin: 0 8px;
  border-radius: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
  }

  &--failed {
    background-color: rgba(var(--v-theme-error), 0.04);
  }

  &--ready {
    background-color: rgba(var(--v-theme-success), 0.04);
  }

  &__visual {
    position: relative;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  &__progress-ring {
    position: absolute;
    top: 0;
    left: 0;
    width: 44px;
    height: 44px;
    transform: rotate(-90deg);
  }

  &__progress-bg {
    stroke: rgba(var(--v-theme-on-surface), 0.08);
  }

  &__progress-bar {
    stroke: rgb(var(--v-theme-primary));
    transition: stroke-dashoffset 0.3s ease;
    stroke-linecap: round;
  }

  &__thumbnail {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.1),
      rgba(var(--v-theme-primary), 0.04)
    );
  }

  &__icon {
    color: rgb(var(--v-theme-primary));
    opacity: 0.85;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.87);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  &__source {
    flex-shrink: 0;
    font-weight: 500;
    padding: 0 6px;
    border-radius: 999px;
    background: rgba(var(--v-theme-primary), 0.1);
    color: rgb(var(--v-theme-primary));
  }

  &__separator {
    opacity: 0.5;
  }

  &__detail {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__status {
    flex-shrink: 0;

    &--error {
      color: rgb(var(--v-theme-error));
    }

    &--success {
      color: rgb(var(--v-theme-success));
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover &__actions {
    opacity: 1;
  }
}
</style>
